import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BuildType, PagedResponse, QueryFiltersParams, QueryPagingParams } from 'mbs-ui-shared@models';
import { ChangeCompanyInfo, CreateUserDto, DeleteUserMode, UpdateUserDto, User, UserComputerData } from 'mbs-ui-shared@models/user';
import {
  DeleteUserRequestType,
  DeleteUsersGroupActionParamsType,
  EnableUsersGroupActionParamsType,
  UsersADRequest,
  UsersQueryParams
} from 'mbs-ui-shared@services/users/users.types';
import { Observable } from 'rxjs';
import { MANUAL_ERROR_HANDLED } from '../../interceptors/error-handler.interceptor';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private http = inject(HttpClient);

  private baseHref = '/api/users';

  get(params?: UsersQueryParams): Observable<PagedResponse<User>> {
    return this.http.get<PagedResponse<User>>(this.baseHref, { params });
  }

  getCompact(params?: UsersQueryParams): Observable<PagedResponse<User>> {
    return this.http.get<PagedResponse<User>>(`${this.baseHref}/compact`, { params });
  }

  getById(id: string): Observable<User> {
    return this.http.get<User>(`${this.baseHref}/${id}`);
  }

  create(params: CreateUserDto): Observable<User> {
    return this.http.post<User>(this.baseHref, params);
  }

  updateUser(params: UpdateUserDto, id: string): Observable<User> {
    return this.http.put<User>(`${this.baseHref}/${id}`, params);
  }

  enable(user: User, enabled: boolean): Observable<null> {
    return this.http.put<null>(`${this.baseHref}/${user.id}/enable`, {
      enabled
    });
  }

  deleteUser({ userId, deleteUserMode, computers, token }: DeleteUserRequestType): Observable<null> {
    const headers = token
      ? {
          Authorization: `${token['token_type']} ${token['access_token']}`,
          CustomAuth: 'true'
        }
      : {};

    return this.http.delete<null>(`${this.baseHref}/${userId}`, {
      body: { mode: deleteUserMode, ...(deleteUserMode === DeleteUserMode.DataOnly ? { computersData: computers } : {}) },
      headers
    });
  }

  resetPassword(
    id: string,
    params: {
      notificationEmails: string[];
      signUpNotificationBuildList: BuildType[];
    }
  ): Observable<null> {
    return this.http.post<null>(`${this.baseHref}/${id}/password/reset`, params);
  }

  getADRequests(params: QueryFiltersParams & QueryPagingParams): Observable<PagedResponse<UsersADRequest>> {
    return this.http.get<PagedResponse<UsersADRequest>>(`${this.baseHref}/ad-requests`, { params });
  }

  cancelDeleting(userId: string): Observable<null> {
    return this.http.post<null>(`${this.baseHref}/${userId}/cancel-deleting`, null);
  }

  enableUsers(params: EnableUsersGroupActionParamsType): Observable<null> {
    return this.http.put<null>(`${this.baseHref}/enable`, params);
  }

  deleteUsers(params: DeleteUsersGroupActionParamsType): Observable<null> {
    return this.http.delete<null>(this.baseHref, {
      body: params
    });
  }

  updateCompany(userId: string, params: { companyId: string; leaveLicenseInLastCompany?: boolean }): Observable<User> {
    return this.http.put<User>(`${this.baseHref}/${userId}/company`, params);
  }

  changeCompanyInfo(userId: string): Observable<ChangeCompanyInfo> {
    return this.http.get<ChangeCompanyInfo>(`${this.baseHref}/${userId}/company/change-company-info`);
  }

  export(body: { users?: string[]; filter?: QueryFiltersParams }): Observable<string> {
    return this.http.post<string>(`${this.baseHref}/export`, body, {
      responseType: 'text' as 'json'
    });
  }

  getUserComputersData(id: string): Observable<UserComputerData[]> {
    return this.http.get<UserComputerData[]>(`${this.baseHref}/${id}/computers-data`);
  }

  changePassword(id: string, params: { password: string; logoutUser: boolean }): Observable<null> {
    return this.http.post<null>(`${this.baseHref}/${id}/password`, params, {
      headers: new HttpHeaders({ MANUAL_ERROR_HANDLED })
    });
  }

  isUserExists(email: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseHref}/exists`, {
      params: { email }
    });
  }

  acceptADRequest(id: string): Observable<null> {
    return this.http.post<null>(`${this.baseHref}/ad-requests/${id}/accept`, null);
  }

  declineADRequest(id: string): Observable<null> {
    return this.http.post<null>(`${this.baseHref}/ad-requests/${id}/decline`, null);
  }

  deleteADRequest(id: string): Observable<null> {
    return this.http.delete<null>(`${this.baseHref}/ad-requests/${id}`);
  }
}
